/**
 * OpenClaw Gateway API
 * Base URL: https://mario-claw.staging.kuaishou.com/api
 * Swagger:  https://mario-claw.staging.kuaishou.com/api/swagger-ui/index.html
 *
 * 开发环境通过 rsbuild proxy /claw-api/ → http://163.7.5.215:18789 解决跨域。
 */

import type {
  AgentIdentityRequest,
  AgentInfo,
  AgentSendRequest,
  AgentsListRequest,
  BotInitResponse,
  BotStatusResponse,
  ChannelStatus,
  ChatAbortRequest,
  ChatCompletionsRequest,
  ChatHistoryRequest,
  ChatMessage,
  ChatSendRequest,
  ClawApiResponse,
  HealthData,
  HealthRequest,
  ModelInfo,
  ResponsesRequest,
  SessionCompactRequest,
  SessionDeleteRequest,
  SessionInfo,
  SessionPatchRequest,
  SessionResetRequest,
  SessionsListRequest,
  SessionsPreviewRequest,
  ToolsInvokeRequest,
  UsageCostData,
  UsageCostRequest,
} from './types';

export interface ClawVersionDownloadResponse {
  win?: string;
  arm?: string;
  intel?: string;
}

// ─── 配置 ─────────────────────────────────────────────────────────────────────

const BASE_URL =
  process.env.NODE_ENV === 'production' ? `https://${window.location.hostname}/claw/api` :
  '/claw/api';

// ─── 内部基础请求 ──────────────────────────────────────────────────────────────

function buildHeaders(sessionKey?: string): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (sessionKey) {
    (headers as Record<string, string>)['x-openclaw-session-key'] = sessionKey;
  }
  return headers;
}

/** 通用 JSON 请求，返回解析后的数据 */
async function request<T>(
  path: string,
  options: {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    body?: unknown;
    sessionKey?: string;
    pathParams?: Record<string, string>;
  } = {},
): Promise<ClawApiResponse<T>> {
  const { method = 'POST', body, sessionKey, pathParams } = options;

  let url = `${BASE_URL}${path}`;
  if (pathParams) {
    Object.entries(pathParams).forEach(([key, val]) => {
      url = url.replace(`{${key}}`, encodeURIComponent(val));
    });
  }

  const res = await fetch(url, {
    method,
    headers: buildHeaders(sessionKey),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    throw new Error(`ClawAPI ${method} ${path} failed: ${res.status} ${res.statusText}`);
  }

  return res.json() as Promise<ClawApiResponse<T>>;
}

/** 流式请求，返回原始 Response（调用方用 response.body 消费 SSE） */
async function streamRequest(
  path: string,
  body: unknown,
  sessionKey?: string,
): Promise<Response> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: buildHeaders(sessionKey),
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    throw new Error(`ClawAPI stream POST ${path} failed: ${res.status} ${res.statusText}`);
  }

  return res;
}

export async function getClawVersionDownloadUrls(): Promise<Required<ClawVersionDownloadResponse>> {
  const res = await fetch('/claw/api/claw/version', {
    method: 'GET',
  });

  if (!res.ok) {
    throw new Error(
      `ClawAPI GET /claw/api/claw/version failed: ${res.status} ${res.statusText}`,
    );
  }

  const data = (await res.json()) as ClawVersionDownloadResponse;
  return {
    win: data.win ?? '',
    arm: data.arm ?? '',
    intel: data.intel ?? '',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Chat 会话
// Chat 消息的发送、历史查询与运行控制
// ─────────────────────────────────────────────────────────────────────────────

/**
 * POST /chat/send
 * 发送 WebSocket 聊天消息。
 * 支持流式响应，返回 Response 供调用方用 XStream 或 response.body.getReader() 消费。
 */
export async function chatSend(params: ChatSendRequest): Promise<Response> {
  const { sessionKey, ...body } = params;
  return streamRequest('/chat/send', body, sessionKey);
}

/**
 * POST /chat/history
 * 查询会话历史消息列表。
 */
export async function chatHistory(
  params: ChatHistoryRequest,
): Promise<ClawApiResponse<ChatMessage[]>> {
  const { sessionKey, ...body } = params;
  return request<ChatMessage[]>('/chat/history', { body, sessionKey });
}

/**
 * POST /chat/abort
 * 终止当前 Agent 运行，中止正在进行的会话任务。
 */
export async function chatAbort(
  params: ChatAbortRequest,
): Promise<ClawApiResponse<void>> {
  const { sessionKey, ...body } = params;
  return request<void>('/chat/abort', { body, sessionKey });
}

// ─────────────────────────────────────────────────────────────────────────────
// Session 管理
// 管理 OpenClaw 会话的生命周期
// 注意：sessions.* 方法使用 key 标识会话，chat.* 方法使用 sessionKey 标识会话
// ─────────────────────────────────────────────────────────────────────────────

/**
 * POST /sessions/list
 * 查询历史会话列表，支持按 Key 列表或时间范围过滤。
 */
export async function sessionsList(
  params: SessionsListRequest,
): Promise<ClawApiResponse<SessionInfo[]>> {
  return request<SessionInfo[]>('/sessions/list', { body: params });
}

/**
 * POST /sessions/preview
 * 预览会话内容（摘要行），可选清空会话历史。
 */
export async function sessionsPreview(
  params: SessionsPreviewRequest,
): Promise<ClawApiResponse<string[]>> {
  return request<string[]>('/sessions/preview', { body: params });
}

/**
 * POST /sessions/patch
 * 更新会话元数据，如推理级别、用量显示策略等。
 */
export async function sessionsPatch(
  params: SessionPatchRequest,
): Promise<ClawApiResponse<void>> {
  return request<void>('/sessions/patch', { body: params });
}

/**
 * POST /sessions/reset
 * 重置会话状态，清空运行时上下文但保留会话记录。
 */
export async function sessionsReset(
  params: SessionResetRequest,
): Promise<ClawApiResponse<void>> {
  return request<void>('/sessions/reset', { body: params });
}

/**
 * POST /sessions/delete
 * 删除指定会话，可选同时清空会话历史。
 */
export async function sessionsDelete(
  params: SessionDeleteRequest,
): Promise<ClawApiResponse<void>> {
  return request<void>('/sessions/delete', { body: params });
}

/**
 * POST /sessions/compact
 * 压缩会话上下文，将历史消息归纳压缩以节省 token。
 */
export async function sessionsCompact(
  params: SessionCompactRequest,
): Promise<ClawApiResponse<void>> {
  return request<void>('/sessions/compact', { body: params });
}

// ─────────────────────────────────────────────────────────────────────────────
// Agent 管理
// 管理和操作 OpenClaw Agent
// ─────────────────────────────────────────────────────────────────────────────

/**
 * POST /agents/list
 * 列出所有可用的 Agent，支持按条件过滤。
 */
export async function agentsList(
  params?: AgentsListRequest,
): Promise<ClawApiResponse<AgentInfo[]>> {
  return request<AgentInfo[]>('/agents/list', { body: params ?? {} });
}

/**
 * POST /agent/send
 * 向指定 Agent 发送消息，触发 Agent 运行。
 */
export async function agentSend(params: AgentSendRequest): Promise<Response> {
  const { sessionKey, ...body } = params;
  return streamRequest('/agent/send', body, sessionKey);
}

/**
 * POST /agent/identity
 * 获取 Agent 的身份信息（名称、描述、能力等）。
 */
export async function agentIdentity(
  params: AgentIdentityRequest,
): Promise<ClawApiResponse<Record<string, unknown>>> {
  const { sessionKey, ...body } = params;
  return request<Record<string, unknown>>('/agent/identity', { body, sessionKey });
}

// ─────────────────────────────────────────────────────────────────────────────
// OpenClaw HTTP 代理
// 将请求直接转发到 OpenClaw 服务的 HTTP 端点
// ─────────────────────────────────────────────────────────────────────────────

/**
 * POST /v1/chat/completions
 * OpenAI Chat Completions API 代理，支持流式输出。
 * 服务端通过 x-openclaw-session-key 维护会话上下文。
 */
export async function chatCompletions(
  params: ChatCompletionsRequest,
  sessionKey?: string,
): Promise<Response> {
  return streamRequest('/v1/chat/completions', params, sessionKey);
}

/**
 * POST /v1/responses
 * OpenAI Responses API 代理，支持流式输出。
 */
export async function responses(
  params: ResponsesRequest,
  sessionKey?: string,
): Promise<Response> {
  return streamRequest('/v1/responses', params, sessionKey);
}

/**
 * POST /tools/invoke
 * 调用指定工具（如 cron.list、skills.status 等）。
 */
export async function toolsInvoke(
  params: ToolsInvokeRequest,
): Promise<ClawApiResponse<unknown>> {
  return request<unknown>('/tools/invoke', { body: params });
}

/**
 * POST /hook/**
 * Hook 代理，将请求转发到 OpenClaw Hook 端点。
 * @param hookPath hook 子路径，如 "agent/hook"
 */
export async function hookProxy(
  hookPath: string,
  body: unknown,
  sessionKey?: string,
): Promise<ClawApiResponse<unknown>> {
  return request<unknown>(`/hook/${hookPath}`, { body, sessionKey });
}

// ─────────────────────────────────────────────────────────────────────────────
// 系统与运维
// OpenClaw 系统状态查询、模型管理、频道状态、用量监控，以及通用 RPC 代理
// ─────────────────────────────────────────────────────────────────────────────

/**
 * POST /openclaw/status
 * 查询 OpenClaw 服务状态。
 */
export async function openclawStatus(): Promise<ClawApiResponse<Record<string, unknown>>> {
  return request<Record<string, unknown>>('/openclaw/status', { body: {} });
}

/**
 * POST /openclaw/health
 * 查询 OpenClaw 服务健康状态，可指定探针 key。
 */
export async function openclawHealth(
  params?: HealthRequest,
): Promise<ClawApiResponse<HealthData>> {
  return request<HealthData>('/openclaw/health', { body: params ?? {} });
}

/**
 * POST /models/list
 * 列出所有可用的 AI 模型。
 */
export async function modelsList(): Promise<ClawApiResponse<ModelInfo[]>> {
  return request<ModelInfo[]>('/models/list', { body: {} });
}

/**
 * POST /channels/status
 * 查询各频道（渠道）的连接状态。
 */
export async function channelsStatus(): Promise<ClawApiResponse<ChannelStatus[]>> {
  return request<ChannelStatus[]>('/channels/status', { body: {} });
}

/**
 * POST /usage/status
 * 查询当前用量状态（已用 / 配额）。
 */
export async function usageStatus(): Promise<ClawApiResponse<Record<string, unknown>>> {
  return request<Record<string, unknown>>('/usage/status', { body: {} });
}

/**
 * POST /usage/cost
 * 查询用量费用明细，支持按日期范围过滤。
 */
export async function usageCost(
  params?: UsageCostRequest,
): Promise<ClawApiResponse<UsageCostData>> {
  return request<UsageCostData>('/usage/cost', { body: params ?? {} });
}

/**
 * POST /rpc/{method}
 * 通用 RPC 代理，将请求转发到 OpenClaw 的指定 RPC 方法。
 * @param method RPC 方法名
 * @param body RPC 请求体
 */
export async function rpcProxy(
  method: string,
  body?: unknown,
): Promise<ClawApiResponse<unknown>> {
  return request<unknown>('/rpc/{method}', {
    body,
    pathParams: { method },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// KIM 消息号绑定
// ─────────────────────────────────────────────────────────────────────────────

export type KimBindStatus =
  | 'NOT_BOUND'
  | 'PENDING'
  | 'KIM_APP_CREATING'
  | 'PLUGIN_DOWNLOADING'
  | 'PLUGIN_INSTALLING'
  | 'PLUGIN_CONFIGURING'
  | 'CHANNEL_POLICY_CONFIGURING'
  | 'CHANNEL_CHECKING'
  | 'READY'
  | 'ERROR';

export interface KimBindStatusData {
  status: KimBindStatus;
  errorMessage?: string;
  appId?: string;
  kimBotId?: string;
  appName?: string;
  iconKey?: string;
}

/**
 * POST /kim/app/v1/bind
 * 触发 KIM 消息号绑定流程。
 */
export async function kimBind(): Promise<ClawApiResponse<void>> {
  return request<void>('/kim/app/v1/bind', { body: {} });
}

/**
 * GET /kim/app/v1/bind/status
 * 查询 KIM 绑定进度状态。
 */
export async function kimBindStatus(): Promise<KimBindStatusData> {
  // @ts-ignore
  return request('/kim/app/v1/bind/status', { method: 'GET' });
}

// ─────────────────────────────────────────────────────────────────────────────
// 内容处理
// ─────────────────────────────────────────────────────────────────────────────

export interface SessionsSummarizeRequest {
  /** 待摘要的文本内容 */
  content: string;
  /** 摘要标题的最大字符数 */
  size?: number;
}

export interface SessionsSummarizeData {
  title: string;
}

/**
 * POST /sessions/summarize
 * 对给定文本内容生成摘要标题。
 */
export async function sessionsSummarize(
  params: SessionsSummarizeRequest,
): Promise<ClawApiResponse<SessionsSummarizeData>> {
  return request<SessionsSummarizeData>('/sessions/summarize', { body: params });
}

// ─────────────────────────────────────────────────────────────────────────────
// Bot 管理
// ─────────────────────────────────────────────────────────────────────────────

/**
 * POST /openclaw/bot/init
 * 初始化 OpenClaw Bot，返回 Bot 信息与初始会话 key。
 */
export async function botInit(
  body?: Record<string, unknown>,
): Promise<ClawApiResponse<BotInitResponse>> {
  return request<BotInitResponse>('/openclaw/bot/init', { body: body ?? {} });
}

/**
 * GET /openclaw/bot/status
 * 查询 Bot 当前运行状态。
 */
export async function botStatus(): Promise<ClawApiResponse<BotStatusResponse>> {
  return request<BotStatusResponse>('/openclaw/bot/status', { method: 'GET' });
}

// ─────────────────────────────────────────────────────────────────────────────
// 保留：原有 clawChatCompletions（兼容旧调用方）
// ─────────────────────────────────────────────────────────────────────────────

/** @deprecated 请改用 chatCompletions() */
export interface ClawChatCompletionsParams {
  sessionId: string;
  content: string;
  stream?: boolean;
}

/** @deprecated 请改用 chatCompletions() */
export async function clawChatCompletions(
  params: ClawChatCompletionsParams,
): Promise<Response> {
  return chatCompletions(
    {
      model: 'auto',
      messages: [{ role: 'user', content: params.content }],
      stream: params.stream ?? true,
    },
    params.sessionId,
  );
}
